import path from "path";
import _ from "lodash";
import moment from "moment";
import fs from "fs";

function base64Encode(file) {
    return fs.readFileSync(file, {encoding: 'base64'});
}

function getBase64EncodedUrlImage(fileName) {
    const imagePath = path.join(global.appRoot, global.isProd ? "buildServer/img" : "src/img", fileName);
    const ext = path.extname(imagePath).slice(1);
    const bas64 = base64Encode(imagePath)
    return `data:image/${ext};base64,${bas64}`
}

const templateDir = path.join(global.appRoot, global.isProd ? 'buildServer' : 'src', '/server/models/cnda/adhesion/templates');

const getPdfConfig = (template, context, pdfName, config) => ({
    content: [{ path: template }],
    context,
    configuration: {
        outputInDB: true,
        templateDir,
        pdfName,
        margin: {
            top: '0px',
            bottom: '0px',
            right: '0px',
            left: '0px',
        },
        footer: {
            height: '0cm',
            html: ''
        },
    }
});

export async function generateMembershipDocuments(contribution, organization) {
    const {code, name, address, additionalAddress, zipCode, city} = organization;

    const organizationAddressElements = _.compact([name, address, additionalAddress, `${zipCode} ${city}`]).map(_.capitalize)

    const commonContext = {
        exerciseCode: _.get(contribution, 'exercise.code'),
        organizationCode: code,
        organizationName: name,
        organizationAddress: address,
        organizationAdditionalAddress: additionalAddress,
        organizationZipCode: zipCode,
        organizationCity: city,
        feeAmount: _.get(contribution, 'feeAmount', 0),
        donationAmount: _.get(contribution, 'donationAmount', 0),
        paymentDate: moment(_.get(contribution, 'paymentDate')).format('DD/MM/YYYY'),
        paymentMethod: _.get(contribution, 'paymentMethod.name'),
        currentDate: moment().format('DD/MM/YYYY'),
        cndaLogo: getBase64EncodedUrlImage('logo_cnda.png'),
        cndaStamp: getBase64EncodedUrlImage('stamp_cnda.png'),
        presidentSignature: getBase64EncodedUrlImage('cnda_president_signature.png'),
        concatenatedAddress: organizationAddressElements.join(', ')
    };


    /*
    {{ organizationName }}\n
            RUE DES LEUPS
            ROUTE DE BELGIQUE
            {{organizationZipCode}} {{organizationCity}}
     */
    const cardConfig = getPdfConfig(
        'membership_card.html',
        commonContext,
        `Carte membre-${commonContext.exerciseCode}-${commonContext.organizationCode} ${commonContext.organizationName}.pdf`
    );
    const statementConfig = getPdfConfig(
        'account_statement.html',
        commonContext,
        `Justificatif-${commonContext.exerciseCode}-${commonContext.organizationCode} ${commonContext.organizationName}.pdf`
    );

    const generatePdfPromise = (config) => new Promise((resolve, reject) => {
        global.pdf.generatePdf(config, (err, file) => {
            if (err) return reject(err);
            resolve(file);
        });
    });

    const [cardFile, statementFile] = await Promise.all([
        generatePdfPromise(cardConfig),
        generatePdfPromise(statementConfig)
    ]);

    return { cardFile, statementFile };
}

export async function sendMembershipEmail(contribution, organization, documents, context) {
    const referents = _.get(contribution, 'organizationReferents', []);
    if (!referents?.length) return;


    const baseUrl = global.isProd
        ? `https://${context.host}`
        : `http://localhost:3000`
    const moduleUrl = `${baseUrl}/business/${context.groupModel?.id}/menuTitle_myAssociation/m-C-myOrganization`

    const donationAmount = _.get(contribution, 'donationAmount', 0)

    const mails = referents.map(referent => {
        return {
            from: '"CNDA" <secretariat@defensedelanimal.fr>',
            to: referent.mail,
            subject: {
                template: donationAmount
                    ? `CNDA ${_.get(contribution, 'exercise.code')} : Carte de membre, Quittance de cotisation annuelle et Reçu fiscal`
                    : `CNDA ${_.get(contribution, 'exercise.code')} : Carte de membre et Quittance de cotisation annuelle`,
            },
            content: 'membership_confirmation.html', // Assuming this template exists
            templateDir,
            context: {
                firstname: referent.firstname,
                amount: _.get(contribution, 'amount'),
                donationAmount,
                exerciseCode: _.get(contribution, 'exercise.code'),
                organizationName: organization.name,
                moduleUrl
            },
            attachments: [
                { id: new global.ObjectID(documents.cardFile.id), filename: documents.cardFile.filename },
                { id: new global.ObjectID(documents.statementFile.id), filename: documents.statementFile.filename },
            ],
        };
    });

    return new Promise((resolve, reject) => {
        global.mailer.sendMail(mails, (error, info) => {
            if (error) {
                console.error('Email sending error:', error);
                return reject(error);
            }
            resolve(info);
        });
    });
}

export async function sendInformEmail(contribution, organization) {
    const referents = _.get(contribution, 'organizationReferents', []);
    if (!referents?.length) return;

    const mails = referents.map(referent => {
        return {
            from: '"CNDA" <secretariat@defensedelanimal.fr>',
            to: referent.mail,
            subject: {template: `Cotisation ${_.get(contribution, 'exercise.code')} : Règlement de cotisation non conforme à la taille de votre association`},
            content: 'inform_referents.html', // Using the new template
            templateDir,
            context: {
                firstname: referent.firstname,
                amount: _.get(contribution, 'amount'),
                exerciseCode: _.get(contribution, 'exercise.code'),
                organizationName: organization.name
            },
        };
    });

    return new Promise((resolve, reject) => {
        global.mailer.sendMail(mails, (error, info) => {
            if (error) {
                console.error('Email sending error:', error);
                return reject(error);
            }
            resolve(info);
        });
    });
}

export async function sendCancelEmail(contribution, organization) {
    const referents = _.get(contribution, 'organizationReferents', []);
    if (!referents?.length) return;

    const mails = referents.map(referent => {
        return {
            from: '"CNDA" <secretariat@defensedelanimal.fr>',
            to: referent.mail,
            subject: {template: `Cotisation ${_.get(contribution, 'exercise.code')} : Erreur d’affectation de règlement`},
            content: 'cancel_payment.html',
            templateDir,
            context: {
                firstname: referent.firstname,
                amount: _.get(contribution, 'amount'),
                donationAmount: _.get(contribution, 'donationAmount', 0),
                exerciseCode: _.get(contribution, 'exercise.code'),
                organizationName: organization.name
            },
        };
    });

    return new Promise((resolve, reject) => {
        global.mailer.sendMail(mails, (error, info) => {
            if (error) {
                console.error('Email sending error:', error);
                return reject(error);
            }
            resolve(info);
        });
    });
}
